// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FunnelAnalysisPage
 * @package @geenius/analytics/solidjs
 * @description Page-level SolidJS composition for configuring and reviewing
 * analytics funnel analysis.
 */

import {
  type AnalyticsEvent,
  analyzeFunnel,
  buildAnalyticsSeoMeta,
  type FunnelResult,
  type FunnelStep,
} from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/solidjs";
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { FunnelChart } from "../components/FunnelChart";
import { StatsCard } from "../components/StatsCard";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import { FadeIn } from "../lib/FadeIn";
import type { AnalyticsSeoMeta } from "./EventExplorerPage";
import { PageErrorBoundary } from "./PageErrorBoundary";

/**
 * Props for rendering the SolidJS funnel analysis page.
 *
 * @property name Human-readable funnel name used when analyzing raw events.
 * @property steps Ordered funnel step configuration shown in the builder.
 * @property events Optional raw events used by the built-in analyze action.
 * @property result Optional precomputed funnel result to visualize.
 * @property onAnalyze Called after the built-in analyze action computes a result.
 * @property isLoading Whether the analysis surface should show loading state.
 * @property error Optional page-level error message or error object.
 * @property onRetry Optional retry handler rendered with the error state.
 * @property renderSeoMeta Optional renderer for host apps that mount SEO metadata.
 * @property class Optional class name merged into the page wrapper.
 */
export interface FunnelAnalysisPageProps {
  name?: string;
  steps?: FunnelStep[];
  events?: AnalyticsEvent[];
  result?: FunnelResult | null;
  onAnalyze?: (result: FunnelResult) => void;
  isLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => unknown;
  class?: string;
}

const AnalyticsSeoHead: Component<{ meta: unknown }> = (props) => {
  void props.meta;
  return null;
};

function formatPercent(value: number | undefined): string {
  return `${(value ?? 0).toFixed(1)}%`;
}

/**
 * Renders a page-level funnel builder and conversion analysis surface.
 *
 * @param props Funnel configuration, raw events, and optional precomputed result.
 * @returns A SolidJS page composition for funnel analysis workflows.
 */
export const FunnelAnalysisPage: Component<FunnelAnalysisPageProps> = (
  props,
) => {
  const [computedResult, setComputedResult] = createSignal<FunnelResult | null>(
    null,
  );
  const copy = createAnalyticsCopy();
  const name = () => props.name ?? copy.t("analytics.funnel.defaultName");
  const steps = () => props.steps ?? [];
  const events = () => props.events ?? [];
  const activeResult = () => props.result ?? computedResult();
  const canAnalyze = () => events().length > 0 && steps().length > 0;
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.t("analytics.seo.funnelTitle"),
    description: copy.t("analytics.seo.funnelDescription"),
    path: "/analytics/funnels",
    keywords: ["analytics", "funnels", "conversion", "events"],
  });
  const stepRows = createMemo(() =>
    steps().map((step, index) => ({
      ...step,
      order: index + 1,
      propertyCount: Object.keys(step.properties ?? {}).length,
    })),
  );

  const runAnalysis = (): void => {
    const nextResult = analyzeFunnel(events(), steps(), name());
    setComputedResult(nextResult);
    props.onAnalyze?.(nextResult);
  };

  return (
    <>
      {props.renderSeoMeta ? (
        props.renderSeoMeta(seoMeta)
      ) : (
        <AnalyticsSeoHead meta={seoMeta} />
      )}
      <div
        class={cn(
          "flex min-h-screen flex-col gap-6 bg-background p-6 text-foreground",
          props.class,
        )}
        data-analytics-page="funnel-analysis"
      >
        <FadeIn>
          <header class="flex flex-col gap-1" data-analytics-page-header>
            <h1 class="text-3xl font-bold tracking-tight">
              {copy.t("analytics.funnel.pageTitle")}
            </h1>
            <p class="text-sm text-muted-foreground">
              {copy.t("analytics.funnel.pageSubtitle")}
            </p>
          </header>
        </FadeIn>

        <PageErrorBoundary error={props.error} onRetry={props.onRetry}>
          <FadeIn
            delay={60}
            class="grid gap-4 sm:grid-cols-3"
            data-analytics-funnel-kpis
          >
            <Show
              when={!props.isLoading}
              fallback={
                <For each={[1, 2, 3]}>
                  {() => (
                    <Card data-analytics-funnel-kpi-loading>
                      <CardContent class="grid gap-3 pt-6">
                        <Skeleton class="h-4 w-24" />
                        <Skeleton class="h-8 w-32" />
                      </CardContent>
                    </Card>
                  )}
                </For>
              }
            >
              <StatsCard
                label={copy.t("analytics.funnel.entries")}
                value={activeResult()?.totalEntries.toLocaleString() ?? "0"}
                icon="IN"
              />
              <StatsCard
                label={copy.t("analytics.dashboard.conversions")}
                value={activeResult()?.totalConversions.toLocaleString() ?? "0"}
                icon="OK"
              />
              <StatsCard
                label={copy.t("analytics.dashboard.conversionRate")}
                value={formatPercent(activeResult()?.overallConversionRate)}
                icon="%"
              />
            </Show>
          </FadeIn>

          <div class="grid gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
            <FadeIn delay={120}>
              <Card data-analytics-funnel-builder>
                <CardHeader class="pb-0">
                  <CardTitle data-analytics-section-title>{name()}</CardTitle>
                  <CardDescription>
                    {copy.plural(
                      "analytics.funnel.configuredSteps",
                      stepRows().length,
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent class="grid gap-4 pt-4">
                  <Show
                    when={stepRows().length > 0}
                    fallback={
                      <EmptyState
                        bordered={false}
                        compact
                        title={copy.t("analytics.funnel.noSteps")}
                      />
                    }
                  >
                    <div class="grid gap-3">
                      <For each={stepRows()}>
                        {(step) => (
                          <article
                            class="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3"
                            data-analytics-funnel-step
                          >
                            <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                              {step.order}
                            </span>
                            <div class="min-w-0">
                              <strong class="text-sm text-foreground">
                                {step.name}
                              </strong>
                              <p class="text-xs text-muted-foreground">
                                {step.event} -{" "}
                                {copy.plural(
                                  "analytics.funnel.propertyFilters",
                                  step.propertyCount,
                                )}
                              </p>
                            </div>
                          </article>
                        )}
                      </For>
                    </div>
                  </Show>
                  <div>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={!canAnalyze() || props.isLoading}
                      onClick={runAnalysis}
                    >
                      {copy.t("analytics.funnel.analyze")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>

            <FadeIn delay={180}>
              <Show
                when={!props.isLoading}
                fallback={
                  <div data-analytics-funnel-loading>
                    <Card>
                      <CardContent class="grid gap-4 pt-6">
                        <Skeleton class="h-5 w-36" />
                        <Skeleton class="h-48 w-full" />
                      </CardContent>
                    </Card>
                  </div>
                }
              >
                <Show
                  when={activeResult()}
                  fallback={
                    <Card data-analytics-funnel-empty>
                      <CardContent class="pt-6">
                        <EmptyState
                          bordered={false}
                          compact
                          title={copy.t("analytics.funnel.noAnalysis")}
                        />
                      </CardContent>
                    </Card>
                  }
                >
                  {(result) => <FunnelChart result={result()} />}
                </Show>
              </Show>
            </FadeIn>
          </div>
        </PageErrorBoundary>
      </div>
    </>
  );
};
